import { useCallback, useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { OBJECT_TYPE } from "common/constants";
import {
  GetWorkflowsForPropertyDocument,
  useCreateWorkflowMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { CommonModal } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";

type CreateWorkflowModalProps = {
  property: { id: string; fullAddress?: string | null };
  hideModal: () => void;
};

type FormData = {
  workflowDefinitionId: string;
};

export const CreateWorkflowModal = ({
  property,
  hideModal,
}: CreateWorkflowModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin, account } = useContext(AuthContext);

  const { control } = useForm<FormData>();

  const [createWorkflowMutation] = useCreateWorkflowMutation({
    onError: () => {
      addErrorToast(
        `There was an issue creating your workflow. Please try again or contact support@withforerunner.com`,
      );
    },
    refetchQueries: [
      {
        query: GetWorkflowsForPropertyDocument,
        variables: { propertyId: property.id },
      },
    ],
  });

  const handleWorkflowClick = useCallback(
    async (workflowDefinitionId: string) => {
      const result = await createWorkflowMutation({
        variables: {
          data: {
            workflowDefinitionId,
            objectId: property.id,
            objectType: OBJECT_TYPE.PROPERTY,
          },
        },
      });

      if (result.data) {
        addSuccessToast("Workflow created successfully");
        hideModal();
        track("Workflow created", {
          workflowId: result.data.createWorkflow.id,
          workflowDefinitionId,
          propertyId: property.id,
          userId: user?.id ?? admin?.id,
          userEmail: user?.email ?? admin?.email,
        });
      }
    },
    [
      createWorkflowMutation,
      property.id,
      addSuccessToast,
      hideModal,
      user?.id,
      user?.email,
      admin?.id,
      admin?.email,
    ],
  );

  const workflowDefinitions = account?.workflowDefinitions ?? [];
  const workflowOptions = workflowDefinitions.map(def => ({
    id: def.id,
    value: def.id,
    title: def.name,
  }));

  return (
    <CommonModal
      title="New workflow"
      onCancel={hideModal}
      subtitle={property?.fullAddress ?? undefined}
      content={
        <Controller
          name="workflowDefinitionId"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <SelectionCard.Group
              handleSelection={(value: string) => {
                onChange(value);
                void handleWorkflowClick(value);
              }}
              direction="vertical"
              spacing="compact"
            >
              <Body size="small" type="emphasis">
                WORKFLOWS
              </Body>
              {workflowOptions.map(option => (
                <SelectionCard.Item
                  key={option.id}
                  value={option.id}
                  borderStyle="ghost"
                  size="compact"
                  data-testid={`workflow-option-${option.id}`}
                >
                  <SelectionCard.CommonContent
                    iconName="folder-closed"
                    title={option.title}
                  />
                </SelectionCard.Item>
              ))}
            </SelectionCard.Group>
          )}
        />
      }
    />
  );
};

export const useCreateWorkflowModal = (property: {
  id: string;
  fullAddress?: string | null;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <CreateWorkflowModal property={property} hideModal={hideModal} />
      </Modal>
    ),
    [property],
  );

  return [showModal, hideModal] as const;
};
